"use client"

import React from "react"
import { X, Filter } from "lucide-react"
import { Button } from "@/shared/components/ui/button"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { workScopeStatusOptions } from "@/features/homeowners/constants/options"
import { getClaimedByDisplayName } from "@/features/homeowners/lib/claimed-by-display"
import { useClaimedByOptions } from "@/features/homeowners/hooks/use-claimed-by-options"
import {
  PRIORITY_FILTER_KEYS,
  getPriorityFiltersFromSearchParams,
  hasActivePriorityFilters,
  clearPriorityFilterParams,
} from "../lib/priority-filters"

function formatYmdLabel(value: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value
  return new Date(`${value}T12:00:00`).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

export default function PriorityFilterIndicator() {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { options: claimedByOptions } = useClaimedByOptions()
  const filters = getPriorityFiltersFromSearchParams(searchParams)

  if (!hasActivePriorityFilters(filters)) return null

  const chips: { key: string; label: string; display: string }[] = []
  if (filters.addedFrom) {
    chips.push({ key: PRIORITY_FILTER_KEYS.addedFrom, label: "Date Entered (from)", display: formatYmdLabel(filters.addedFrom) })
  }
  if (filters.addedTo) {
    chips.push({ key: PRIORITY_FILTER_KEYS.addedTo, label: "Date Entered (to)", display: formatYmdLabel(filters.addedTo) })
  }
  if (filters.claimedBy.length) {
    const labels = filters.claimedBy.map((value) => {
      const option = claimedByOptions.find((opt) => opt.value === value)
      return option?.label || getClaimedByDisplayName(value) || value
    })
    chips.push({
      key: PRIORITY_FILTER_KEYS.claimedBy,
      label: "Claimed By",
      display: labels.join(", "),
    })
  }
  if (filters.workscopeStatus.length) {
    const labels = filters.workscopeStatus.map((value) => {
      const option = workScopeStatusOptions.find((opt) => opt.value === value)
      return option?.label || value
    })
    chips.push({
      key: PRIORITY_FILTER_KEYS.workscopeStatus,
      label: "Status of Workscope",
      display: labels.join(", "),
    })
  }

  const removeFilter = (key: string) => {
    const next = new URLSearchParams(searchParams.toString())
    next.delete(key)
    const query = next.toString()
    router.push(query ? `${pathname}?${query}` : pathname)
  }

  const clearAll = () => {
    const next = clearPriorityFilterParams(searchParams)
    const query = next.toString()
    router.push(query ? `${pathname}?${query}` : pathname)
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Filter className="w-4 h-4" />
        <span>Active filters:</span>
      </div>
      {chips.map((chip) => (
        <div
          key={chip.key}
          className="flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-sm"
        >
          <span className="font-medium">{chip.label}:</span>
          <span>{chip.display}</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto p-0 ml-1 hover:bg-primary/20"
            onClick={() => removeFilter(chip.key)}
          >
            <X className="w-3 h-3" />
          </Button>
        </div>
      ))}
      <Button
        variant="default"
        size="sm"
        onClick={clearAll}
        className="bg-primary hover:bg-primary/90 text-white font-bold whitespace-nowrap"
      >
        Clear all
      </Button>
    </div>
  )
}
